/**
 * liveMeterModel — what the /desktop live meter says (usage-metering step 11, 2026-10-20). PURE: useLiveMeter.ts reads
 * the two documents and the clock, and both /desktop pages draw the chip's words from here, as they draw the status
 * strip's (DESK_STATUS_COPY) and the source pill's (describeSource).
 *
 * Its sources:
 *   accounts/{id}/live_meters/{uid}   the person's own conversation with Prest0n as it runs (written by the voice
 *                                     gateway, functions/src/voiceGateway/liveMeter.ts; only its person reads it)
 *   accounts/{id}/vm/main.vmAwake     the workspace's workstations' awake hours this month (written hourly by
 *                                     functions/src/maintenance/vmSpendSweep.ts), plus a live tail while the machine on
 *                                     screen is awake
 *
 * Its words (Miles, 2026-10-19):
 *   talking 4:12 · face 1:05 · $0.31                      a conversation going
 *   last conversation 10:02 · face 2:40 · $0.71           one that ended today
 *   … · test example — nothing billed                    one whose debits were test examples
 *   workstation 112 h awake this month · included         the machine ("workstations" when there are several)
 * Dollars appear only when the record carries them: the server writes them only for someone who may see their own
 * usage (`usage.view-own`; owners always), never the page's decision. A face that never spoke adds no face time.
 */

/**
 * An open conversation no write has touched for this long is over (its gateway instance went away without ending it).
 * The server's twin is LIVE_METER_STALE_MS in functions/src/voiceGateway/liveMeter.ts, held equal by
 * functions/src/__tests__/aConversationIsMeteredAsItRuns.test.ts.
 */
export const LIVE_METER_STALE_MS = 3 * 60_000;
/** The live tail added to the sweep's count while the machine is awake: the sweep runs hourly, so about an hour and a bit. */
export const AWAKE_TAIL_MAX_MS = 75 * 60_000;

/** The person's conversation, as its record holds it. */
export interface LiveMeterConversation {
  startedAtMs: number;
  endedAtMs: number | null;
  faceMs: number;
  /** What it charged at each debit's stamp; null when the person may not see their own usage's dollars. */
  spentUsd: number | null;
  testMode: boolean;
  updatedAtMs: number;
}

/** The workspace's workstations' awake time this month, as the sweep last counted it. */
export interface LiveMeterAwake {
  /** UTC `YYYY-MM`, the sweep's month. */
  month: string;
  hours: number;
  machines: number;
  asOfMs: number;
}

/** What the chip shows; each part null when it has nothing to say. */
export interface LiveMeterView {
  talk: { open: boolean; talkMs: number; faceMs: number; spentUsd: number | null; testMode: boolean } | null;
  awake: { hours: number; machines: number } | null;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null);

/** PURE. The conversation in a live_meters record, or null when the record holds none the gateway wrote. */
export function liveMeterConversationOf(record: unknown): LiveMeterConversation | null {
  if (!record || typeof record !== 'object') return null;
  const r = record as Record<string, unknown>;
  const c = r.conversation;
  if (!c || typeof c !== 'object') return null;
  const o = c as Record<string, unknown>;
  const startedAtMs = num(o.startedAtMs);
  const updatedAtMs = num(r.updatedAtMs);
  if (!startedAtMs || !updatedAtMs) return null;
  return {
    startedAtMs,
    endedAtMs: num(o.endedAtMs) || null,
    faceMs: num(o.faceMs) ?? 0,
    spentUsd: num(o.spentUsd),
    testMode: o.testMode === true,
    updatedAtMs,
  };
}

/** PURE. The awake time on a vm/main record, or null when the sweep has written none. */
export function liveMeterAwakeOf(vmMain: unknown): LiveMeterAwake | null {
  const a = vmMain && typeof vmMain === 'object' ? (vmMain as Record<string, unknown>).vmAwake : null;
  if (!a || typeof a !== 'object') return null;
  const o = a as Record<string, unknown>;
  const hours = num(o.hours);
  const asOfMs = num(o.asOfMs);
  const machines = num(o.machines);
  if (typeof o.month !== 'string' || !/^\d{4}-\d{2}$/.test(o.month) || hours === null || !asOfMs) return null;
  return { month: o.month, hours, machines: machines && machines >= 1 ? Math.floor(machines) : 1, asOfMs };
}

/** PURE. A conversation is going: not ended, and touched within LIVE_METER_STALE_MS. */
export function conversationOpen(c: LiveMeterConversation, nowMs: number): boolean {
  return c.endedAtMs === null && nowMs - c.updatedAtMs <= LIVE_METER_STALE_MS;
}

function utcMonth(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function sameLocalDay(a: number, b: number): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

/**
 * PURE. What the chip shows at `nowMs`, or null when it has nothing to say.
 *  - The conversation: going (talk time runs to now), or over (to its end; one the gateway never ended, to its last
 *    write) and shown only on the same local day it ended.
 *  - The awake time: the sweep's count for this month, plus the time since while the machine on screen is awake
 *    (`awakeNow`), at most AWAKE_TAIL_MAX_MS. A count from an earlier month says nothing about this one.
 */
export function liveMeterView(input: {
  conversation: LiveMeterConversation | null;
  awake: LiveMeterAwake | null;
  awakeNow: boolean;
  nowMs: number;
}): LiveMeterView | null {
  const { conversation: c, awake: a, nowMs } = input;
  let talk: LiveMeterView['talk'] = null;
  if (c) {
    const open = conversationOpen(c, nowMs);
    const endMs = open ? nowMs : (c.endedAtMs ?? c.updatedAtMs);
    if (open || sameLocalDay(endMs, nowMs)) {
      talk = { open, talkMs: Math.max(0, endMs - c.startedAtMs), faceMs: c.faceMs, spentUsd: c.spentUsd, testMode: c.testMode };
    }
  }
  let awake: LiveMeterView['awake'] = null;
  if (a && a.month === utcMonth(nowMs)) {
    const tailMs = input.awakeNow ? Math.min(AWAKE_TAIL_MAX_MS, Math.max(0, nowMs - a.asOfMs)) : 0;
    awake = { hours: Math.round(a.hours + tailMs / 3_600_000), machines: a.machines };
  }
  return talk || awake ? { talk, awake } : null;
}

/** "4:12", or "1:04:12" past the hour. */
export function meterClock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

// copy: desktop_status_chrome
/** PURE. The chip's words: the conversation's part, then the machine's, one line. */
export function liveMeterWords(view: LiveMeterView): string {
  const parts: string[] = [];
  if (view.talk) {
    parts.push(`${view.talk.open ? 'talking' : 'last conversation'} ${meterClock(view.talk.talkMs)}`);
    if (view.talk.faceMs > 0) parts.push(`face ${meterClock(view.talk.faceMs)}`);
    if (view.talk.spentUsd !== null) {
      parts.push(view.talk.testMode ? 'test example — nothing billed' : `$${view.talk.spentUsd.toFixed(2)}`);
    }
  }
  if (view.awake) {
    parts.push(`${view.awake.machines > 1 ? 'workstations' : 'workstation'} ${view.awake.hours} h awake this month · included`);
  }
  return parts.join(' · ');
}
